import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { usePrintStudio } from '../../state/printStudio';
import { useWorldStore } from '../../state/worldStore';
import { edgeInset, IS_GUEST } from '../../config';
import { get } from './studioApi';
import { T, Row, Select, Note, Spinner, Btn, shield, site } from './ui';

// PrintStudio — THE HYPEFLYER PRINT SHOP'S STUDIO (2026-10-31).
//
// Khari: "We are going to change the laudromat into a flyer and graphics shop.
// Users will go there and be able to make images, posts, whois cards and
// presskits. For the images portion, duplicate the features that are currently
// in the image studio modal. We can have a front desk with a clerk and next to
// them can be the options on a panel/modal as they walk you through them."
//
// This is that panel. It stands beside the clerk's chat (ShopDock) and makes
// what the website makes, on the website's endpoints and at its prices, for an
// artist the visitor owns:
//   Images    the Create Image studio, all of it (ImagesSection)
//   Posts     Create Post: Write, Speak, Track, YouTube (PostsSection)
//   Who Is    the Who Is card, a HypeFlyer (WhoIsSection)
//   Press kit create and manage (PressKitSection)
// The clerk turns it (API PrintShopService show_studio → state/printStudio) and
// never presses anything: every make is the visitor's own button, under its
// price. Opened by the counter, by the clerk, or by tapping the studio screen
// in the room (PrintShopScreen). Gone when you walk out.
//
// Each section is its own chunk, loaded when opened: the studio costs the
// city's first load nothing.
const SECTIONS = {
  images: { label: 'Images', blurb: 'A headshot, a scene, a full-body pose, track art — or bring a picture to life.', C: lazy(() => import('./ImagesSection')) },
  posts: { label: 'Posts', blurb: 'Write one, have them speak it, share a track, or post a video to YouTube.', C: lazy(() => import('./PostsSection')) },
  whois: { label: 'Who Is', blurb: 'Six cards that introduce your artist, in their own words — with a narrated video.', C: lazy(() => import('./WhoIsSection')) },
  presskit: { label: 'Press kit', blurb: 'A page for press and curators, written from your artist’s real story.', C: lazy(() => import('./PressKitSection')) },
};
const ORDER = ['images', 'posts', 'whois', 'presskit'];
const PICK_KEY = 'world:printshop:artist';
const NARROW = 820;

const readPick = () => { try { return Number(localStorage.getItem(PICK_KEY)) || null; } catch { return null; } };
const writePick = (id) => { try { localStorage.setItem(PICK_KEY, String(id)); } catch { /* a remembered pick is a convenience */ } };

/** The window is narrow (a phone): the studio stands on top instead of beside the dock. */
function useNarrow() {
  const [n, setN] = useState(() => typeof window !== 'undefined' && window.innerWidth < NARROW);
  useEffect(() => {
    const on = () => setN(window.innerWidth < NARROW);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return n;
}

export default function PrintStudio() {
  const open = usePrintStudio((s) => s.open);
  const zone = usePrintStudio((s) => s.zone);
  const section = usePrintStudio((s) => s.section);
  const prefill = usePrintStudio((s) => s.prefill);
  const go = usePrintStudio((s) => s.go);
  const close = usePrintStudio((s) => s.close);
  const reset = usePrintStudio((s) => s.reset);
  const interior = useWorldStore((s) => s.interior);
  const dockHere = useWorldStore((s) => !!(s.shop && s.shop.zone === zone));
  const narrow = useNarrow();

  // Walking out closes it (the counter belongs to a room, and so does this).
  useEffect(() => { if (zone && interior !== zone) reset(); }, [interior, zone, reset]);

  // ── WHICH ARTIST ──────────────────────────────────────────────────────────
  const [avatars, setAvatars] = useState(null);     // null = loading, [] = none
  const [aid, setAid] = useState(readPick);
  const [avatar, setAvatar] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    if (!open || IS_GUEST || avatars) return;
    let dead = false;
    get('/api/avatars').then((r) => {
      if (dead) return;
      if (!r.ok) { setLoadErr(r.data?.error || 'Could not load your artists.'); setAvatars([]); return; }
      const list = (r.data.avatars || []).filter((a) => a && a.avatar_id != null);
      setAvatars(list);
      setAid((cur) => (list.some((a) => Number(a.avatar_id) === Number(cur)) ? cur : (list[0]?.avatar_id ?? null)));
    });
    return () => { dead = true; };
  }, [open, avatars]);

  const refreshAvatar = useCallback(async () => {
    if (!aid) return null;
    const r = await get(`/api/avatars/${aid}`);
    const av = r.ok ? (r.data.avatar || null) : null;
    if (av) setAvatar(av);
    return av;
  }, [aid]);
  const refreshTracks = useCallback(async () => {
    if (!aid) return [];
    const r = await get(`/api/tracks/avatar/${aid}`);
    const list = r.ok ? (r.data.tracks || []) : [];
    setTracks(list);
    return list;
  }, [aid]);
  // Loaded when first opened for an artist, and kept through a close (closing
  // only hides the studio while you are in the shop).
  const loadedFor = React.useRef(null);
  useEffect(() => {
    if (!aid) { loadedFor.current = null; setAvatar(null); setTracks([]); return; }
    if (!open || loadedFor.current === aid) return;
    loadedFor.current = aid;
    writePick(aid);
    setAvatar(null);
    refreshAvatar();
    refreshTracks();
  }, [open, aid, refreshAvatar, refreshTracks]);

  /* ⚠ A SECTION STAYS MOUNTED ONCE OPENED (hidden when another is showing).
     Some makes answer only in their response (a post's generated picture is
     not saved anywhere until it is posted), so unmounting a section mid-make
     would lose what the visitor paid for. Reset per artist: a different
     artist starts every section fresh. */
  const [opened, setOpened] = useState([]);
  useEffect(() => { setOpened([]); }, [aid]);
  useEffect(() => { if (section && !opened.includes(section)) setOpened((cur) => [...cur, section]); }, [section, opened]);

  // What a section made, for the clerk's chat (ShopDock shows it as its note line).
  const onNote = useCallback((line) => {
    if (!line) return;
    window.dispatchEvent(new CustomEvent('ht:shop-note', { detail: String(line) }));
  }, []);

  const frame = useMemo(() => {
    const base = {
      position: 'fixed', zIndex: 71, pointerEvents: 'auto', display: 'flex', flexDirection: 'column',
      background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16, overflow: 'hidden',
      boxShadow: '0 22px 70px rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
      fontFamily: T.font, color: T.ink,
    };
    if (narrow) return { ...base, left: 8, right: 8, top: 'max(8px, env(safe-area-inset-top))', maxHeight: '62vh' };
    // Beside the clerk's dock (340 wide, 16 in from the edge) when it is open; at the edge when not.
    return {
      ...base,
      right: dockHere ? `calc(${edgeInset(16, 'right')} + 352px)` : edgeInset(16, 'right'),
      bottom: 'calc(max(16px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))',
      width: 'min(440px, calc(100vw - 32px))',
      maxHeight: 'calc(100vh - 96px - var(--hud-player-h, 0px))',
    };
  }, [narrow, dockHere]);

  // Closed is HIDDEN while you are still in the shop (the same reason sections
  // stay mounted: a make in flight answers into this tree); leaving unmounts it.
  if (!open && !zone) return null;
  const S = section ? SECTIONS[section] : null;
  const picked = avatars?.find((a) => Number(a.avatar_id) === Number(aid)) || null;

  return (
    <div style={{ ...frame, display: open ? 'flex' : 'none' }} {...shield} role="dialog" aria-label="HypeFlyer Print Shop studio" aria-hidden={!open}>
      {/* Header: the shop, the four sections, close */}
      <div style={{ padding: '11px 13px 9px', borderBottom: `1px solid ${T.lineSoft}` }}>
        <Row wrap={false} style={{ justifyContent: 'space-between' }}>
          <button type="button" onClick={() => go(null)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ color: T.hot, font: `900 15px ${T.font}`, letterSpacing: 0.4 }}>HYPEFLYER <span style={{ color: T.sun }}>PRINT SHOP</span></div>
            <div style={{ color: T.faint, font: `11px ${T.font}`, marginTop: 1 }}>{S ? S.label : 'What are we making?'}</div>
          </button>
          <button type="button" onClick={close} aria-label="Close the studio" style={{ background: 'none', border: 'none', color: T.dim, fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1 }}>×</button>
        </Row>
        {!IS_GUEST && (
          <Row gap={6} style={{ marginTop: 9 }}>
            {ORDER.map((k) => (
              <button key={k} type="button" onClick={() => go(k)}
                style={{ flex: '1 1 0', padding: '6px 4px', borderRadius: 999, cursor: 'pointer', font: `700 12px ${T.font}`,
                  color: section === k ? '#170910' : T.ink, background: section === k ? T.hot : T.field, border: `1px solid ${section === k ? T.hot : T.lineSoft}` }}>
                {SECTIONS[k].label}
              </button>
            ))}
          </Row>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 13px 14px' }}>
        {IS_GUEST ? (
          <GuestNote />
        ) : avatars === null ? (
          <Row gap={10} style={{ padding: '20px 0' }}><Spinner /><span style={{ color: T.dim, font: `13px ${T.font}` }}>Finding your artists…</span></Row>
        ) : !avatars.length ? (
          <NoArtist error={loadErr} />
        ) : (
          <>
            {/* Who it is for */}
            <Row gap={8} wrap={false} style={{ marginBottom: 6 }}>
              {picked?.profile_image_url
                ? <img src={picked.profile_image_url} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flex: '0 0 auto' }} />
                : <div style={{ width: 30, height: 30, borderRadius: '50%', background: T.field, flex: '0 0 auto' }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select value={String(aid ?? '')} onChange={(v) => setAid(Number(v))}
                  options={avatars.map((a) => ({ value: String(a.avatar_id), label: `${a.name || 'Untitled artist'}${a.is_group ? ' (band)' : ''}` }))} />
              </div>
            </Row>
            {!S ? (
              <Home onGo={go} />
            ) : !avatar ? (
              <Row gap={10} style={{ padding: '20px 0' }}><Spinner /><span style={{ color: T.dim, font: `13px ${T.font}` }}>Opening {S.label.toLowerCase()}…</span></Row>
            ) : (
              <Suspense fallback={<Row gap={10} style={{ padding: '20px 0' }}><Spinner /><span style={{ color: T.dim, font: `13px ${T.font}` }}>Opening {S.label.toLowerCase()}…</span></Row>}>
                {/* Every section opened for this artist stays mounted; only the current one shows.
                    Keyed on the artist: switching artists starts each section fresh. */}
                {ORDER.filter((k) => k === section || opened.includes(k)).map((k) => {
                  const C = SECTIONS[k].C;
                  return (
                    <div key={`${k}:${avatar.avatar_id}`} style={{ display: k === section ? 'block' : 'none' }}>
                      <C
                        avatar={avatar}
                        tracks={tracks}
                        refreshAvatar={refreshAvatar}
                        refreshTracks={refreshTracks}
                        prefill={k === section ? prefill : null}
                        onNote={onNote}
                        onGo={go}
                      />
                    </div>
                  );
                })}
              </Suspense>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** The four things made here, before one is chosen. */
function Home({ onGo }) {
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
      {ORDER.map((k) => (
        <button key={k} type="button" onClick={() => onGo(k)}
          style={{ textAlign: 'left', cursor: 'pointer', padding: '12px 13px', borderRadius: 12, background: 'rgba(255,79,163,0.07)', border: `1px solid ${T.line}`, color: T.ink }}>
          <div style={{ font: `800 14.5px ${T.font}` }}>{SECTIONS[k].label}</div>
          <div style={{ font: `12.5px/1.4 ${T.font}`, color: T.dim, marginTop: 3 }}>{SECTIONS[k].blurb}</div>
        </button>
      ))}
      <Note>The clerk can walk you through any of these — just ask at the counter. Prices are under each button; nothing is made until you press it.</Note>
    </div>
  );
}

function GuestNote() {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ font: `700 14px ${T.font}`, marginBottom: 6 }}>Sign in to make things here</div>
      <Note>Everything made at the print shop is for an artist you own — pictures, posts, Who Is cards and press kits. Sign in to HypeTunes and come back through the door.</Note>
      <a href={site('/login')} target="_blank" rel="noreferrer" style={{ color: T.sun, font: `700 13px ${T.font}` }}>Sign in ↗</a>
    </div>
  );
}

function NoArtist({ error }) {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ font: `700 14px ${T.font}`, marginBottom: 6 }}>{error ? 'Could not load your artists' : 'You have no artist yet'}</div>
      <Note tone={error ? 'error' : 'info'}>{error || 'The print shop makes things for an artist you own. Create one on HypeTunes, then come back.'}</Note>
      {!error && <a href={site('/avatars')} target="_blank" rel="noreferrer" style={{ color: T.sun, font: `700 13px ${T.font}` }}>Create an artist ↗</a>}
      {error && <Btn kind="ghost" onClick={() => window.location.reload()}>Reload</Btn>}
    </div>
  );
}
